import { Component, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import Task = gapi.client.tasks.Task;

import { runInAngular } from '../run-in-angular';
import { StoreService } from '../store.service';
import { NameDialogComponent } from '../name-dialog/name-dialog.component';
import { TasksDeleteParams, TasksInsertParams, TasksPatchParams, TasksMoveParams } from '../api/tasks-resource-method-params';

const DEBUG = false;

@Component({
  selector: 'app-subtasks',
  templateUrl: './subtasks.component.html',
  styleUrls: ['./subtasks.component.scss']
})
export class SubtasksComponent {

  steps$ = new Subject<Task[]>();
  parent?: Task

  constructor(
    public dialog: MatDialog,
    private store: StoreService,
    private ngZone: NgZone) {
    if (DEBUG) {console.log('new SubtasksComponent()');}
    this.store.task$.subscribe((parent: Task|null) => {
      this.parent = parent  ? parent : undefined;
      this.store.tasks$.pipe(
        map(tasks => tasks?.items ? tasks.items : []),
        map(array => array.filter(task => parent ? task.parent===parent.id : false)),
        map((array: Task[]) => array.sort((a: Task, b: Task)=> {
          /* eslint-disable @typescript-eslint/no-non-null-assertion */
          const first = Number.parseInt(a.position!);
          const second = Number.parseInt(b.position!);
          /* eslint-enable @typescript-eslint/no-non-null-assertion */
          if (DEBUG) console.log('Comparing %d to %d', first, second);
          return (first - second);
        })),
        runInAngular(this.ngZone)
      ).subscribe(items => {
        this.steps$.next(items);
      });
    });
  }

  onSelect(step: Task): void {
    if (DEBUG) {console.log('Step selected:', step);}
    this.store.onStepSelected(step);
  }

  addStep(): void {
    const dialogRef = this.dialog.open(NameDialogComponent, {
      width: '600px'
    });
    dialogRef.afterClosed().subscribe((data: string) => {
      if (data) {
        const request: TasksInsertParams = {
          tasklist: this.store.taskList!.id!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          parent: this.parent?.id,
          resource: {title: data}
        };
        void this.store.onStepAdded(request);
      }
    });
  }

  drop($event: CdkDragDrop<Task[]|null, Task[]>): void {
    // Required paramters: [tasklist, task, parent]
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const tasklist = this.store.taskList!.id!;
    const parent = this.parent!.id!;
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    const step = $event.item.data.id;
    const request: TasksMoveParams = {
      tasklist: tasklist,
      parent: parent,
      task: step,
    };

    // Optional parameters: [previous]
    //eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const steps: Task[] = $event.container.data!;
    const priorIndex = $event.currentIndex-1;
    const priorId = steps[priorIndex]?.id;
    if (priorId) {
      request.previous = priorId;
    }
    if (DEBUG) {console.log('Created move request:',request);}

    if ($event.previousContainer===$event.container) {
      moveItemInArray(steps,
                      $event.previousIndex,
                      $event.currentIndex);
    } else {
      const tasks: Task[] = $event.previousContainer.data;
      transferArrayItem(tasks,
                        steps,
                        $event.previousIndex,
                        $event.currentIndex);
    }
    this.store.onTaskMoved(request);
  }

  onDeleted(step: Task): void {
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const request: TasksDeleteParams = {
      tasklist: this.store.taskList!.id!,
      task: step.id!
    };
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    void this.store.onStepDeleted(request);
  }

  onUndeleted(task: Task): void {
    if (DEBUG) {console.log('Undeleting task:', task);}
    task.deleted = false;
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const request: TasksPatchParams = {
      tasklist: this.store.taskList!.id!,
      task: task.id!,
      resource: task
    };
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    void this.store.onStepUpdated(request);
  }

  onCompleted(task: Task): void {
    /* eslint-disable @typescript-eslint/no-non-null-assertion */
    const request: TasksPatchParams = {
      tasklist: this.store.taskList!.id!,
      task: task.id!,
      resource: {status: task.status}
    };
    /* eslint-enable @typescript-eslint/no-non-null-assertion */
    void this.store.onStepUpdated(request);
  }
}
